import type { PageHelp } from '../../help/types';

/** Page guide for `/projects/:code/imports` (spec §9.5, §12A). */
export const importsHelp: PageHelp = {
  title: 'Imports',
  intro:
    'Bulk upload of translations from a simple_json file, one locale per file, and the history that makes it safe. Nothing is written until you approve the preview, and every applied batch keeps the values it replaced so it can be reverted.',
  sections: [
    'History',
    'Upload: choose a file',
    'Upload: options',
    'Upload: check the diff',
    'Upload: apply',
    'Batch details',
  ],
  contexts: {
    'upload-dialog': 'Click “Upload” at the top right.',
    'upload-preview': 'Click “Upload”, choose a file and click “Preview”.',
    'batch-detail': 'Click “Diff” on a row of the history.',
    'batch-every-row': 'Click “Diff” on a row of the history, then open the “Every row” tab.',
  },
  tips: [
    'A file may be at most 10 MB and 20,000 rows; split a larger one into several uploads.',
    'A grant narrowed to some locales also limits imports: a file in any other locale is refused.',
    'A row for a key this project links from another project (Linked keys) is skipped as LINKED_KEY: its values belong to that project and are changed there. Re-importing this project’s own export therefore does not fail on its linked keys.',
    'A row of a subscribed package is skipped as PACKAGE_KEY: the package’s value wins. PACKAGE_KEY_EMPTY means the package has no value in this locale, so the project shows the fallback there until somebody translates the key in the package. If the package’s text does not suit this project, create a key with the project’s own prefix and translate that instead.',
    'A new key marked KEY_REVIVED was deleted earlier (for example by reverting an upload): applying brings that key back instead of creating a second one, and reverting the upload deletes it again.',
  ],
  steps: [
    // --- History ---------------------------------------------------------------------------
    {
      selector: '[data-help="imports-upload"]',
      title: 'Start an upload',
      body: 'Opens the three-step import dialog: choose a file and its locale, check the diff, then apply. The button is enabled for owners and translators of the project and for admins; everyone else only sees the history.',
      section: 'History',
      i18n: {
        'de-AT': {
          title: 'Upload starten',
          body: 'Öffnet den dreistufigen Import-Dialog: Datei und Locale wählen, Diff prüfen, dann anwenden. Die Schaltfläche ist für Owner und Translator des Projekts sowie für Admins aktiv; alle anderen sehen nur die Historie.',
        },
        'sr-Latn-RS': {
          title: 'Pokrenite otpremanje',
          body: 'Otvara uvoz u tri koraka: izaberite fajl i njegov locale, proverite diff, pa primenite. Dugme je aktivno za owner i translator ovog projekta i za administratore; svi ostali vide samo istoriju.',
        },
      },
    },
    {
      selector: '[data-help="imports-history"]',
      title: 'Read the import history',
      body: 'Every upload of this project is listed here, newest first, with the file, the locale, who ran it and when. A batch that was previewed but never applied stays in the list and wrote nothing; a revert is recorded as a batch of its own.',
      section: 'History',
      i18n: {
        'de-AT': {
          title: 'Import-Historie lesen',
          body: 'Hier steht jeder Upload dieses Projekts, der neueste zuerst, mit Datei, Locale, ausführender Person und Zeitpunkt. Ein Batch, der nur in der Vorschau war und nie angewendet wurde, bleibt in der Liste und hat nichts geschrieben; ein Revert wird als eigener Batch festgehalten.',
        },
        'sr-Latn-RS': {
          title: 'Pregledajte istoriju uvoza',
          body: 'Ovde je svako otpremanje ovog projekta, od najnovijeg, sa fajlom, locale-om, osobom koja ga je pokrenula i vremenom. Batch koji je samo pregledan, a nikad primenjen, ostaje na listi i nije ništa upisao; revert se beleži kao poseban batch.',
        },
      },
    },
    {
      selector: '[data-help="imports-status"]',
      title: 'Read the status of a batch',
      body: '“previewed” means the plan was stored but not applied yet, “applied” means it was written, and “reverted” means a later revert put its cells back. “revert” marks the batch a revert created. “failed” means the preview was reported as an error because the file could not be stored; it can never be applied, so upload the file again. A previewed batch can still be applied from its row; only an applied upload can be reverted.',
      section: 'History',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Status eines Batches lesen',
          body: '„previewed“ heißt, der Plan wurde gespeichert, aber noch nicht angewendet; „applied“ heißt, er wurde geschrieben; „reverted“ heißt, ein späterer Revert hat seine Zellen zurückgesetzt. „revert“ kennzeichnet den Batch, den ein Revert angelegt hat. „failed“ heißt, die Vorschau wurde als Fehler gemeldet, weil die Datei nicht gespeichert werden konnte; ein solcher Batch lässt sich nie anwenden – laden Sie die Datei erneut hoch. Ein Batch im Status „previewed“ kann in seiner Zeile noch angewendet werden; zurücksetzen lässt sich nur ein angewendeter Upload.',
        },
        'sr-Latn-RS': {
          title: 'Pročitajte status batch-a',
          body: '„previewed“ znači da je plan sačuvan, ali još nije primenjen, „applied“ da je upisan, a „reverted“ da je kasniji revert vratio njegove ćelije. „revert“ označava batch koji je napravio revert. „failed“ znači da je pregled prijavljen kao greška jer fajl nije mogao da se sačuva; takav batch nikad ne može da se primeni, pa fajl otpremite ponovo. Batch u statusu „previewed“ još može da se primeni iz svog reda; vratiti se može samo primenjen upload.',
        },
      },
    },
    {
      selector: '[data-help="imports-counts"]',
      title: 'Check the row counts',
      body: 'Created counts new keys and Updated counts changed values. Skipped adds up every row that was refused or left out with a reason — a duplicate, an unknown prefix, a protected reviewed value, a QA error. An amber number there is worth opening with “Diff”.',
      section: 'History',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Zeilenzahlen prüfen',
          body: '„Created“ zählt neue Keys, „Updated“ geänderte Werte. „Skipped“ summiert jede Zeile, die mit Begründung abgelehnt oder ausgelassen wurde – ein Duplikat, ein unbekanntes Präfix, ein geschützter reviewter Wert, ein QA-Fehler. Eine gelbe Zahl dort sollten Sie mit „Diff“ öffnen.',
        },
        'sr-Latn-RS': {
          title: 'Proverite brojeve redova',
          body: '„Created“ broji nove ključeve, a „Updated“ izmenjene vrednosti. „Skipped“ sabira svaki red koji je odbijen ili izostavljen uz razlog – duplikat, nepoznat prefiks, zaštićena reviewed vrednost, QA greška. Žuti broj u toj koloni vredi otvoriti preko „Diff“.',
        },
      },
    },
    {
      selector: '[data-help="imports-diff"]',
      title: 'Open what a batch did',
      body: 'Opens the batch with its summary and the row-level diff, the previous value struck through above the new one. Use it before a revert to see exactly what would be undone.',
      section: 'History',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Ansehen, was ein Batch getan hat',
          body: 'Öffnet den Batch mit Zusammenfassung und zeilenweisem Diff, der vorherige Wert durchgestrichen über dem neuen. Verwenden Sie das vor einem Revert, um genau zu sehen, was rückgängig gemacht würde.',
        },
        'sr-Latn-RS': {
          title: 'Otvorite šta je batch uradio',
          body: 'Otvara batch sa rezimeom i diff-om po redovima, gde je prethodna vrednost precrtana iznad nove. Koristite ga pre revert-a da vidite tačno šta bi bilo poništeno.',
        },
      },
    },
    {
      selector: '[data-help="imports-apply-previewed"]',
      title: 'Apply a preview later',
      body: 'Shown only on a “previewed” row — an upload whose dialog was closed before “Apply”. After you confirm, the stored plan is written exactly as its diff shows, as if you had clicked “Apply” in the dialog. If any of those translations was edited since the preview, nothing is written (IMPORT_STALE) and the file has to be uploaded again. Disabled when the preview has nothing to write; available to owners, translators and admins. Should the server need longer than a minute, the confirmation reads “Waiting for the server…” until the batch is applied.',
      section: 'History',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Vorschau später anwenden',
          body: 'Erscheint nur in einer Zeile mit Status „previewed“ – ein Upload, dessen Dialog vor „Apply“ geschlossen wurde. Nach Ihrer Bestätigung wird der gespeicherte Plan genau so geschrieben, wie sein Diff zeigt, als hätten Sie im Dialog „Apply“ geklickt. Wurde eine dieser Übersetzungen seit der Vorschau bearbeitet, wird nichts geschrieben (IMPORT_STALE) und die Datei muss erneut hochgeladen werden. Deaktiviert, wenn die Vorschau nichts zu schreiben hat; verfügbar für Owner, Translator und Admins. Braucht der Server länger als eine Minute, zeigt die Bestätigung „Waiting for the server…“, bis der Batch angewendet ist.',
        },
        'sr-Latn-RS': {
          title: 'Primenite pregled naknadno',
          body: 'Prikazuje se samo u redu sa statusom „previewed“ – otpremanje čiji je dijalog zatvoren pre „Apply“. Kada potvrdite, sačuvani plan se upisuje tačno onako kako ga diff prikazuje, kao da ste kliknuli „Apply“ u dijalogu. Ako je neki od tih prevoda izmenjen posle pregleda, ništa se ne upisuje (IMPORT_STALE) i fajl mora ponovo da se otpremi. Neaktivno kada pregled nema šta da upiše; dostupno za owner, translator i administratore. Ako serveru treba više od minut, potvrda prikazuje „Waiting for the server…“ dok se batch ne primeni.',
        },
      },
    },
    {
      selector: '[data-help="imports-revert"]',
      title: 'Revert a batch',
      body: 'After you confirm, every cell the batch wrote goes back to its previous value; a cell that was empty before is emptied again, and a key the import created is removed once none of its values remain. A cell edited since the import is reported instead of overwritten, so a revert never destroys newer work. Only an applied upload can be reverted, by an owner, translator or admin. A revert batch cannot be reverted in turn — to bring the values back, upload the file again.',
      section: 'History',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Batch zurücksetzen',
          body: 'Nach Ihrer Bestätigung bekommt jede Zelle, die der Batch geschrieben hat, ihren vorherigen Wert zurück; eine vorher leere Zelle wird wieder geleert, und ein vom Import angelegter Key wird entfernt, sobald keiner seiner Werte übrig ist. Eine seit dem Import bearbeitete Zelle wird gemeldet statt überschrieben, ein Revert zerstört also keine neuere Arbeit. Nur ein angewendeter Upload kann zurückgesetzt werden, von Owner, Translator oder Admin. Ein Revert-Batch lässt sich nicht seinerseits zurücksetzen — um die Werte zurückzuholen, laden Sie die Datei erneut hoch.',
        },
        'sr-Latn-RS': {
          title: 'Vratite batch',
          body: 'Kada potvrdite, svaka ćelija koju je batch upisao vraća se na prethodnu vrednost; ćelija koja je ranije bila prazna ponovo se prazni, a ključ koji je uvoz napravio uklanja se kada mu ne ostane nijedna vrednost. Ćelija izmenjena posle uvoza se prijavljuje umesto da bude prepisana, pa revert nikad ne briše noviji rad. Vratiti se može samo primenjen upload, a to mogu owner, translator ili administrator. Revert batch se ne može ponovo vratiti — da biste vratili vrednosti, otpremite fajl ponovo.',
        },
      },
    },

    {
      selector: '[data-help="imports-load-more"]',
      title: 'Load older batches',
      body: 'The history shows the newest 50 batches. When there are more, this button loads the next ones below them.',
      section: 'History',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Ältere Batches laden',
          body: 'Die Historie zeigt die neuesten 50 Batches. Gibt es mehr, lädt diese Schaltfläche die nächsten darunter.',
        },
        'sr-Latn-RS': {
          title: 'Učitajte starije batch-eve',
          body: 'Istorija prikazuje najnovijih 50 batch-eva. Kada ih ima više, ovo dugme učitava sledeće ispod njih.',
        },
      },
    },

    // --- Upload: choose a file -------------------------------------------------------------
    {
      selector: '[data-help="imports-file"]',
      title: 'Choose the file',
      body: 'Pick one .json file holding a flat or nested simple_json object for a single locale — the same shape the export produces. Plural keys may use `key_one`-style suffixes or nested forms.',
      section: 'Upload: choose a file',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Datei wählen',
          body: 'Wählen Sie eine .json-Datei mit einem flachen oder verschachtelten simple_json-Objekt für genau eine Locale – dieselbe Form, die der Export erzeugt. Plural-Keys dürfen Suffixe wie `key_one` oder verschachtelte Formen verwenden.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite fajl',
          body: 'Izaberite jedan .json fajl sa ravnim ili ugnežđenim simple_json objektom za jedan locale – isti oblik koji pravi export. Plural ključevi mogu da koriste sufikse kao `key_one` ili ugnežđene forme.',
        },
      },
    },
    {
      selector: '[data-help="imports-locale"]',
      title: 'Pick the locale of the file',
      body: 'Tells Glossa which language the file contains; only the project’s active locales are offered, and the source locale is marked. A file in the source locale is written as reviewed, because those are the developers’ own texts; any other locale is written as draft.',
      section: 'Upload: choose a file',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Locale der Datei wählen',
          body: 'Gibt an, welche Sprache die Datei enthält; angeboten werden nur die aktiven Locales des Projekts, die Quell-Locale ist markiert. Eine Datei in der Quell-Locale wird als reviewed geschrieben, weil es die eigenen Texte der Entwickler sind; jede andere Locale als draft.',
        },
        'sr-Latn-RS': {
          title: 'Izaberite locale fajla',
          body: 'Određuje na kom jeziku je fajl; nude se samo aktivni locale-i projekta, a izvorni je označen. Fajl na izvornom locale-u upisuje se kao reviewed, jer su to tekstovi samih developera; svaki drugi locale upisuje se kao draft.',
        },
      },
    },
    {
      selector: '[data-help="imports-preview"]',
      title: 'Read the file into a preview',
      body: 'Parses the file, compares every row with the current draft and stores the result as a plan — no translation is written yet. A file that cannot be read as simple_json is reported at the top of the dialog. Every preview also appears in the history as “previewed”.',
      section: 'Upload: choose a file',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Datei in eine Vorschau einlesen',
          body: 'Liest die Datei, vergleicht jede Zeile mit dem aktuellen Entwurf und speichert das Ergebnis als Plan – geschrieben wird noch keine Übersetzung. Eine Datei, die sich nicht als simple_json lesen lässt, wird oben im Dialog gemeldet. Jede Vorschau erscheint außerdem als „previewed“ in der Historie.',
        },
        'sr-Latn-RS': {
          title: 'Učitajte fajl u pregled',
          body: 'Čita fajl, poredi svaki red sa trenutnim nacrtom i čuva rezultat kao plan – nijedan prevod se još ne upisuje. Fajl koji ne može da se pročita kao simple_json prijavljuje se na vrhu dijaloga. Svaki pregled se pojavljuje i u istoriji kao „previewed“.',
        },
      },
    },

    // --- Upload: options -------------------------------------------------------------------
    {
      selector: '[data-help="imports-option-create-keys"]',
      title: 'Create keys that do not exist yet',
      body: 'On (the default) creates a key for every row whose name is new. Off means the import only updates existing keys, and unknown names are skipped with the reason KEY_NOT_FOUND.',
      section: 'Upload: options',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Fehlende Keys anlegen',
          body: 'Eingeschaltet (Standard) wird für jede Zeile mit neuem Namen ein Key angelegt. Ausgeschaltet aktualisiert der Import nur bestehende Keys, unbekannte Namen werden mit dem Grund KEY_NOT_FOUND übersprungen.',
        },
        'sr-Latn-RS': {
          title: 'Napravite ključeve koji ne postoje',
          body: 'Uključeno (podrazumevano) pravi ključ za svaki red sa novim imenom. Isključeno znači da uvoz samo menja postojeće ključeve, a nepoznata imena se preskaču uz razlog KEY_NOT_FOUND.',
        },
      },
    },
    {
      selector: '[data-help="imports-option-overwrite-reviewed"]',
      title: 'Overwrite reviewed translations',
      body: 'Off (the default) skips every row whose current value is reviewed, so a bulk upload cannot replace approved work. Turning it on needs review rights — owner or translator — and the preview is refused for anyone else.',
      section: 'Upload: options',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Reviewte Übersetzungen überschreiben',
          body: 'Ausgeschaltet (Standard) wird jede Zeile übersprungen, deren aktueller Wert reviewed ist, ein Massen-Upload kann also keine freigegebene Arbeit ersetzen. Zum Einschalten brauchen Sie Review-Rechte – Owner oder Translator –, sonst wird die Vorschau abgelehnt.',
        },
        'sr-Latn-RS': {
          title: 'Prepišite reviewed prevode',
          body: 'Isključeno (podrazumevano) preskače svaki red čija je trenutna vrednost reviewed, pa masovno otpremanje ne može da zameni odobren rad. Za uključivanje su potrebna prava za review – owner ili translator – inače se pregled odbija.',
        },
      },
    },
    {
      selector: '[data-help="imports-option-package-keys"]',
      title: 'Write keys owned by a subscribed package',
      body: 'Rows whose prefix belongs to a package this project subscribes to are skipped by default (PACKAGE_KEY, or PACKAGE_KEY_EMPTY when the package has no value in this locale). On writes them into the package, but only for a package you own (or as an admin); for any other package they stay skipped.',
      section: 'Upload: options',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Keys eines abonnierten Pakets schreiben',
          body: 'Zeilen, deren Präfix zu einem vom Projekt abonnierten Paket gehört, werden standardmäßig übersprungen (PACKAGE_KEY, oder PACKAGE_KEY_EMPTY, wenn das Paket in dieser Locale keinen Wert hat). Eingeschaltet werden sie ins Paket geschrieben, aber nur bei einem Paket, dessen Owner Sie sind (oder als Admin); bei jedem anderen Paket bleiben sie übersprungen.',
        },
        'sr-Latn-RS': {
          title: 'Upišite ključeve pretplaćenog paketa',
          body: 'Redovi čiji prefiks pripada paketu na koji je projekat pretplaćen podrazumevano se preskaču (PACKAGE_KEY, ili PACKAGE_KEY_EMPTY kada paket nema vrednost u ovom locale-u). Uključeno ih upisuje u paket, ali samo za paket čiji ste owner (ili kao administrator); za svaki drugi paket ostaju preskočeni.',
        },
      },
    },
    {
      selector: '[data-help="imports-option-register-prefixes"]',
      title: 'Register unknown key prefixes',
      body: 'A key prefix that no project or package owns is refused by default (UNKNOWN_PREFIX). On registers it to this project during the import; a later revert does not release it. Only project owners and admins may turn this on.',
      section: 'Upload: options',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Unbekannte Key-Präfixe registrieren',
          body: 'Ein Key-Präfix, das keinem Projekt und keinem Paket gehört, wird standardmäßig abgelehnt (UNKNOWN_PREFIX). Eingeschaltet wird es beim Import diesem Projekt zugeordnet; ein späterer Revert gibt es nicht wieder frei. Nur Projekt-Owner und Admins dürfen diese Option nutzen.',
        },
        'sr-Latn-RS': {
          title: 'Registrujte nepoznate prefikse ključeva',
          body: 'Prefiks ključa koji ne pripada nijednom projektu ni paketu podrazumevano se odbija (UNKNOWN_PREFIX). Uključeno ga tokom uvoza registruje na ovaj projekat; kasniji revert ga ne oslobađa. Ovu opciju smeju da koriste samo owner projekta i administratori.',
        },
      },
    },
    {
      selector: '[data-help="imports-option-strict"]',
      title: 'Treat a placeholder mismatch as an error',
      body: 'On (the default) refuses a row that fails a QA check — typically {{placeholders}} that differ from the source value — and lists it as an error. Off lets such a row through. Control characters and editor markers are refused either way.',
      section: 'Upload: options',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Platzhalter-Abweichung als Fehler werten',
          body: 'Eingeschaltet (Standard) wird eine Zeile abgelehnt, die eine QA-Prüfung nicht besteht – meist {{Platzhalter}}, die vom Quellwert abweichen –, und als Fehler aufgelistet. Ausgeschaltet geht so eine Zeile durch. Steuerzeichen und Editor-Marker werden in jedem Fall abgelehnt.',
        },
        'sr-Latn-RS': {
          title: 'Neslaganje placeholder-a smatrajte greškom',
          body: 'Uključeno (podrazumevano) odbija red koji ne prođe QA proveru – obično {{placeholder-e}} koji se razlikuju od izvorne vrednosti – i navodi ga kao grešku. Isključeno propušta takav red. Kontrolni znakovi i markeri editora odbijaju se u svakom slučaju.',
        },
      },
    },
    {
      selector: '[data-help="imports-duplicates"]',
      title: 'Decide what happens to duplicate keys',
      body: 'When the same key appears more than once in the file, “Report both, write neither” (the default) skips every copy as DUPLICATE_IN_FILE. “Let the last one win” writes the last occurrence and skips the earlier ones.',
      section: 'Upload: options',
      context: 'upload-dialog',
      i18n: {
        'de-AT': {
          title: 'Mit doppelten Keys umgehen',
          body: 'Kommt derselbe Key mehrmals in der Datei vor, überspringt „Report both, write neither“ (Standard) jede Kopie als DUPLICATE_IN_FILE. „Let the last one win“ schreibt das letzte Vorkommen und überspringt die früheren.',
        },
        'sr-Latn-RS': {
          title: 'Odlučite šta biva sa duplim ključevima',
          body: 'Kada se isti ključ pojavi više puta u fajlu, „Report both, write neither“ (podrazumevano) preskače svaku kopiju kao DUPLICATE_IN_FILE. „Let the last one win“ upisuje poslednje pojavljivanje i preskače ranija.',
        },
      },
    },

    // --- Upload: check the diff ------------------------------------------------------------
    {
      selector: '[data-help="imports-preview-summary"]',
      title: 'Read the summary',
      body: 'Shows the file and its target locale, then the number of rows with new keys, updated, unchanged and skipped ones. Rows that cannot be written are counted in a warning and listed as errors in the diff below, each with its reason.',
      section: 'Upload: check the diff',
      context: 'upload-preview',
      i18n: {
        'de-AT': {
          title: 'Zusammenfassung lesen',
          body: 'Zeigt Datei und Ziel-Locale, dann die Zahl der Zeilen mit neuen Keys, aktualisierten, unveränderten und übersprungenen. Zeilen, die nicht geschrieben werden können, werden in einer Warnung gezählt und im Diff darunter als Fehler mit Begründung aufgelistet.',
        },
        'sr-Latn-RS': {
          title: 'Pročitajte rezime',
          body: 'Prikazuje fajl i ciljni locale, zatim broj redova sa novim ključevima, izmenjenim, nepromenjenim i preskočenim. Redovi koji ne mogu da se upišu broje se u upozorenju i navode kao greške u diff-u ispod, svaki sa razlogom.',
        },
      },
    },
    {
      selector: '[data-help="imports-preview-diff"]',
      title: 'Check each row',
      body: 'One line per row with its action: create key, update, unchanged, skipped or error. For an update the current value is struck through above the new one, and a refused row shows its reason in amber.',
      section: 'Upload: check the diff',
      context: 'upload-preview',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Jede Zeile prüfen',
          body: 'Eine Zeile pro Eintrag mit ihrer Aktion: create key, update, unchanged, skipped oder error. Bei einem Update steht der aktuelle Wert durchgestrichen über dem neuen, eine abgelehnte Zeile zeigt ihren Grund in Gelb.',
        },
        'sr-Latn-RS': {
          title: 'Proverite svaki red',
          body: 'Po jedan red za svaki unos, sa akcijom: create key, update, unchanged, skipped ili error. Kod izmene je trenutna vrednost precrtana iznad nove, a odbijeni red prikazuje razlog žutom bojom.',
        },
      },
    },
    {
      selector: '[data-help="imports-resolutions"]',
      title: 'Subscribe or link the keys of another owner',
      body: 'Shown when the file has rows whose prefix belongs to a package this project does not subscribe to, or keys of another project it does not link yet. Those rows are errors until you tick an entry here: “Subscribe to the package” adds the package to the project (its bundle then carries every key of the package), “Link the keys” links only the file’s keys that the other project has. Both happen when you click “Apply”, in the same step as the import. The owner’s value wins: the rows become PACKAGE_KEY or LINKED_KEY skips and the file’s values for them are not written. Subscribing needs the owner role in this project, linking the owner or developer role; without it the entry is disabled. A key the other project does not have stays an error.',
      section: 'Upload: check the diff',
      context: 'upload-preview',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Keys eines anderen Owners abonnieren oder verlinken',
          body: 'Erscheint, wenn die Datei Zeilen enthält, deren Präfix zu einem Paket gehört, das dieses Projekt nicht abonniert hat, oder Keys eines anderen Projekts, die es noch nicht verlinkt. Diese Zeilen sind Fehler, bis Sie hier einen Eintrag anhaken: „Subscribe to the package“ fügt das Paket dem Projekt hinzu (das Bundle enthält dann jeden Key des Pakets), „Link the keys“ verlinkt nur die Keys der Datei, die das andere Projekt hat. Beides geschieht beim Klick auf „Apply“, im selben Schritt wie der Import. Der Wert des Owners hat Vorrang: Die Zeilen werden als PACKAGE_KEY oder LINKED_KEY übersprungen, die Werte der Datei dafür werden nicht geschrieben. Abonnieren erfordert die Owner-Rolle in diesem Projekt, Verlinken die Owner- oder Developer-Rolle; ohne sie ist der Eintrag inaktiv. Ein Key, den das andere Projekt nicht hat, bleibt ein Fehler.',
        },
        'sr-Latn-RS': {
          title: 'Pretplatite se ili linkujte ključeve drugog vlasnika',
          body: 'Prikazuje se kada fajl ima redove čiji prefiks pripada paketu na koji ovaj projekat nije pretplaćen, ili ključeve drugog projekta koje još ne linkuje. Ti redovi su greške dok ovde ne štiklirate stavku: „Subscribe to the package“ dodaje paket projektu (bundle tada nosi svaki ključ paketa), a „Link the keys“ linkuje samo ključeve iz fajla koje drugi projekat ima. Oba se dešavaju kad kliknete „Apply“, u istom koraku kao uvoz. Vrednost vlasnika ima prednost: redovi postaju PACKAGE_KEY ili LINKED_KEY preskočeni, a vrednosti iz fajla za njih se ne upisuju. Pretplata traži owner ulogu u ovom projektu, a linkovanje owner ili developer ulogu; bez nje je stavka neaktivna. Ključ koji drugi projekat nema ostaje greška.',
        },
      },
    },
    {
      selector: '[data-help="imports-back"]',
      title: 'Go back to change the options',
      body: 'Returns to the first step with the file and options kept, so you can change something and preview again. The preview you leave behind stays in the history as “previewed” and writes nothing unless somebody applies it from there.',
      section: 'Upload: check the diff',
      context: 'upload-preview',
      i18n: {
        'de-AT': {
          title: 'Zurück, um Optionen zu ändern',
          body: 'Führt zum ersten Schritt zurück, Datei und Optionen bleiben erhalten – Sie können etwas ändern und erneut eine Vorschau erstellen. Die verlassene Vorschau bleibt als „previewed“ in der Historie und schreibt nichts, solange sie dort niemand anwendet.',
        },
        'sr-Latn-RS': {
          title: 'Vratite se da promenite opcije',
          body: 'Vraća na prvi korak uz sačuvan fajl i opcije, pa možete nešto da promenite i ponovo napravite pregled. Napušteni pregled ostaje u istoriji kao „previewed“ i ne upisuje ništa dok ga neko odatle ne primeni.',
        },
      },
    },

    // --- Upload: apply ---------------------------------------------------------------------
    {
      selector: '[data-help="imports-apply"]',
      title: 'Apply the changes',
      body: 'Writes exactly the plan you just reviewed, after the subscriptions and links you ticked; the label counts the new keys and updated values. When nothing would be written but a subscription or a link is ticked, it reads just “Apply”. When there is nothing at all, it reads “Nothing to apply” and stays disabled. After applying, the dialog closes and the batch shows in the history as “applied”. If the server needs longer than a minute, the button reads “Still applying on the server…” and the dialog waits for the batch to finish — do not upload the file again; the history shows it as “applied” the moment it is done.',
      section: 'Upload: apply',
      context: 'upload-preview',
      i18n: {
        'de-AT': {
          title: 'Änderungen anwenden',
          body: 'Schreibt genau den Plan, den Sie gerade geprüft haben, nach den angehakten Abonnements und Verlinkungen; die Beschriftung zählt neue Keys und aktualisierte Werte. Würde nichts geschrieben, ist aber ein Abonnement oder eine Verlinkung angehakt, lautet sie nur „Apply“. Gibt es gar nichts zu tun, lautet sie „Nothing to apply“ und bleibt inaktiv. Nach dem Anwenden schließt sich der Dialog und der Batch steht als „applied“ in der Historie. Braucht der Server länger als eine Minute, lautet die Schaltfläche „Still applying on the server…“ und der Dialog wartet, bis der Batch fertig ist – laden Sie die Datei nicht erneut hoch; die Historie zeigt ihn als „applied“, sobald er abgeschlossen ist.',
        },
        'sr-Latn-RS': {
          title: 'Primenite izmene',
          body: 'Upisuje tačno plan koji ste upravo pregledali, posle pretplata i linkova koje ste štiklirali; natpis broji nove ključeve i izmenjene vrednosti. Ako se ništa ne bi upisalo, a štiklirana je pretplata ili link, natpis glasi samo „Apply“. Ako nema ničega, glasi „Nothing to apply“ i ostaje neaktivno. Posle primene dijalog se zatvara, a batch se u istoriji vidi kao „applied“. Ako serveru treba više od minut, dugme glasi „Still applying on the server…“ i dijalog čeka da se batch završi – nemojte ponovo otpremati fajl; istorija ga prikazuje kao „applied“ čim bude gotov.',
        },
      },
    },

    // --- Batch details ---------------------------------------------------------------------
    {
      selector: '[data-help="imports-detail-view"]',
      title: 'Switch between changed rows and every row',
      body: '“Changed only” is the server’s own list of what this batch wrote — the rows a revert would undo. “Every row” adds the unchanged, skipped and error rows, with the reason each was refused.',
      section: 'Batch details',
      context: 'batch-detail',
      i18n: {
        'de-AT': {
          title: 'Zwischen geänderten und allen Zeilen wechseln',
          body: '„Changed only“ ist die Liste des Servers, was dieser Batch geschrieben hat – genau die Zeilen, die ein Revert rückgängig machen würde. „Every row“ zeigt zusätzlich unveränderte, übersprungene und fehlerhafte Zeilen, jeweils mit dem Grund der Ablehnung.',
        },
        'sr-Latn-RS': {
          title: 'Prebacujte između izmenjenih i svih redova',
          body: '„Changed only“ je spisak servera o tome šta je ovaj batch upisao – upravo redovi koje bi revert poništio. „Every row“ dodaje nepromenjene, preskočene i redove sa greškom, svaki sa razlogom odbijanja.',
        },
      },
    },
    {
      selector: '[data-help="imports-detail-filter"]',
      title: 'Filter the rows by action',
      body: 'Narrows “Every row” to one action: create key, update, unchanged, skipped or error. Pick skipped or error to find out why part of a file did not land.',
      section: 'Batch details',
      context: 'batch-every-row',
      i18n: {
        'de-AT': {
          title: 'Zeilen nach Aktion filtern',
          body: 'Schränkt „Every row“ auf eine Aktion ein: create key, update, unchanged, skipped oder error. Wählen Sie skipped oder error, um herauszufinden, warum ein Teil der Datei nicht angekommen ist.',
        },
        'sr-Latn-RS': {
          title: 'Filtrirajte redove po akciji',
          body: 'Sužava „Every row“ na jednu akciju: create key, update, unchanged, skipped ili error. Izaberite skipped ili error da saznate zašto deo fajla nije upisan.',
        },
      },
    },
    {
      selector: '[data-help="imports-detail-rows"]',
      title: 'Read the rows of a batch',
      body: 'The same diff as in the preview: the previous value struck through above the new one, and the reason for every refused row. A long batch shows its first rows and says how many there are in total.',
      section: 'Batch details',
      context: 'batch-detail',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Zeilen eines Batches lesen',
          body: 'Derselbe Diff wie in der Vorschau: der vorherige Wert durchgestrichen über dem neuen und der Grund für jede abgelehnte Zeile. Ein langer Batch zeigt seine ersten Zeilen und nennt die Gesamtzahl.',
        },
        'sr-Latn-RS': {
          title: 'Pročitajte redove batch-a',
          body: 'Isti diff kao u pregledu: prethodna vrednost precrtana iznad nove i razlog za svaki odbijeni red. Dugačak batch prikazuje prve redove i navodi koliko ih ima ukupno.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Importe',
      intro:
        'Massen-Upload von Übersetzungen aus einer simple_json-Datei, eine Locale pro Datei, samt der Historie, die das sicher macht. Geschrieben wird erst, wenn Sie die Vorschau bestätigen, und jeder angewendete Batch behält die ersetzten Werte, damit er zurückgesetzt werden kann.',
      sections: {
        History: 'Historie',
        'Upload: choose a file': 'Upload: Datei wählen',
        'Upload: options': 'Upload: Optionen',
        'Upload: check the diff': 'Upload: Diff prüfen',
        'Upload: apply': 'Upload: anwenden',
        'Batch details': 'Batch-Details',
      },
      contexts: {
        'upload-dialog': 'Klicken Sie rechts oben auf „Upload“.',
        'upload-preview':
          'Klicken Sie auf „Upload“, wählen Sie eine Datei und klicken Sie auf „Preview“.',
        'batch-detail': 'Klicken Sie in einer Zeile der Historie auf „Diff“.',
        'batch-every-row':
          'Klicken Sie in einer Zeile der Historie auf „Diff“ und öffnen Sie dann den Reiter „Every row“.',
      },
      tips: [
        'Eine Datei darf höchstens 10 MB und 20.000 Zeilen haben; teilen Sie eine größere auf mehrere Uploads auf.',
        'Eine auf bestimmte Locales eingeschränkte Berechtigung gilt auch für Importe: Eine Datei in einer anderen Locale wird abgelehnt.',
        'Eine Zeile für einen Key, den dieses Projekt aus einem anderen Projekt verknüpft („Linked keys“), wird als LINKED_KEY übersprungen: Seine Werte gehören jenem Projekt und werden dort geändert. Ein erneuter Import des eigenen Exports scheitert daher nicht an den verknüpften Keys.',
        'Eine Zeile eines abonnierten Pakets wird als PACKAGE_KEY übersprungen: Der Wert des Pakets hat Vorrang. PACKAGE_KEY_EMPTY bedeutet, dass das Paket in dieser Locale keinen Wert hat; das Projekt zeigt dort den Fallback, bis jemand den Key im Paket übersetzt. Passt der Text des Pakets nicht zu diesem Projekt, legen Sie einen Key mit dem eigenen Präfix des Projekts an und übersetzen Sie diesen.',
        'Ein neuer Key mit KEY_REVIVED wurde früher gelöscht (zum Beispiel durch das Zurücksetzen eines Uploads): Das Anwenden holt diesen Key zurück, statt einen zweiten anzulegen, und das Zurücksetzen des Uploads löscht ihn wieder.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Uvozi',
      intro:
        'Masovno otpremanje prevoda iz simple_json fajla, jedan locale po fajlu, i istorija koja to čini bezbednim. Ništa se ne upisuje dok ne odobrite pregled, a svaki primenjen batch čuva vrednosti koje je zamenio, pa može da se vrati.',
      sections: {
        History: 'Istorija',
        'Upload: choose a file': 'Otpremanje: izbor fajla',
        'Upload: options': 'Otpremanje: opcije',
        'Upload: check the diff': 'Otpremanje: provera diff-a',
        'Upload: apply': 'Otpremanje: primena',
        'Batch details': 'Detalji batch-a',
      },
      contexts: {
        'upload-dialog': 'Kliknite na „Upload“ gore desno.',
        'upload-preview': 'Kliknite na „Upload“, izaberite fajl i kliknite na „Preview“.',
        'batch-detail': 'Kliknite na „Diff“ u redu istorije.',
        'batch-every-row': 'Kliknite na „Diff“ u redu istorije, pa otvorite karticu „Every row“.',
      },
      tips: [
        'Fajl može imati najviše 10 MB i 20.000 redova; veći fajl podelite na više otpremanja.',
        'Dozvola ograničena na pojedine locale-e važi i za uvoz: fajl na nekom drugom locale-u se odbija.',
        'Red za ključ koji ovaj projekat povezuje iz drugog projekta („Linked keys“) preskače se kao LINKED_KEY: njegove vrednosti pripadaju tom projektu i menjaju se tamo. Ponovni uvoz sopstvenog exporta zato ne pada na povezanim ključevima.',
        'Red pretplaćenog paketa preskače se kao PACKAGE_KEY: vrednost paketa ima prednost. PACKAGE_KEY_EMPTY znači da paket nema vrednost u ovom locale-u, pa projekat tu prikazuje fallback dok neko ne prevede ključ u paketu. Ako tekst paketa ne odgovara ovom projektu, napravite ključ sa sopstvenim prefiksom projekta i prevedite njega.',
        'Nov ključ označen sa KEY_REVIVED je ranije obrisan (na primer revertom nekog uvoza): primena vraća taj ključ umesto da pravi drugi, a revert tog uvoza ga ponovo briše.',
      ],
    },
  },
};
